<template>
  <Adminlte>
    <div class="box box-primary center-block">
      <div class="box-header with-border">
        <i class="fa fa-wrench font-dark"></i>
        <h3 class="box-title">Manutenção do Sistema</h3>
      </div>

      <section class="content">
        <div class="maintenance-section">
          <h4>Consistência de estágios dos parques</h4>
          <p class="help-text">
            Compara o estágio cadastrado do parque com o estágio calculado a partir das
            datas das Informações Gerais. A sincronização atualiza o cadastro com base nessas datas.
          </p>

          <div class="btn-row">
            <button
              class="btn btn-primary"
              @click="runAudit"
              :disabled="parkStagesLoading"
            >
              Auditar (somente leitura) &nbsp;
              <i class="fa fa-refresh fa-spin" v-if="parkStagesLoading && parkStagesAction === 'audit'"></i>
            </button>

            <button
              class="btn btn-warning"
              @click="runSync"
              :disabled="parkStagesLoading"
            >
              Sincronizar estágios &nbsp;
              <i class="fa fa-refresh fa-spin" v-if="parkStagesLoading && parkStagesAction === 'sync'"></i>
            </button>
          </div>

          <div v-if="parkStagesError" class="alert alert-danger">{{ parkStagesError }}</div>

          <div v-if="parkStagesResult" class="result-box">
            <p>
              <strong>{{ parkStagesResult.fixApplied ? 'Sincronização executada.' : 'Auditoria executada.' }}</strong>
              Total de inconsistências: <strong>{{ parkStagesResult.total }}</strong>
              <span v-if="parkStagesResult.fixApplied"> — Corrigidas: <strong>{{ parkStagesResult.fixed }}</strong></span>
            </p>

            <table v-if="parkStagesResult.inconsistencies.length" class="table table-striped table-bordered">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Parque</th>
                  <th>Problemas</th>
                  <th v-if="parkStagesResult.fixApplied">Corrigido</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in parkStagesResult.inconsistencies" :key="item.id">
                  <td>{{ item.id }}</td>
                  <td>{{ item.name }}</td>
                  <td>
                    <ul class="issues-list">
                      <li v-for="(issue, index) in item.issues" :key="index">{{ issue }}</li>
                    </ul>
                  </td>
                  <td v-if="parkStagesResult.fixApplied">
                    <span :class="item.fixed ? 'label label-success' : 'label label-default'">
                      {{ item.fixed ? 'Sim' : 'Não' }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>

            <p v-else class="text-success">Nenhuma inconsistência encontrada.</p>
          </div>
        </div>

        <hr>

        <div class="maintenance-section">
          <h4>Natureza jurídica da entidade gestora</h4>
          <p class="help-text">
            Atualiza as opções dos campos de natureza jurídica (administrativa e financeira)
            no banco de dados, incluindo a opção <strong>Empresa Pública</strong>.
            Equivalente ao comando <code>inovadata:update-management-entity-legal-nature-options</code>.
          </p>

          <div class="btn-row">
            <button
              class="btn btn-warning"
              @click="runLegalNatureUpdate"
              :disabled="legalNatureLoading"
            >
              Atualizar opções no banco &nbsp;
              <i class="fa fa-refresh fa-spin" v-if="legalNatureLoading"></i>
            </button>
          </div>

          <div v-if="legalNatureError" class="alert alert-danger">{{ legalNatureError }}</div>

          <div v-if="legalNatureResult" class="alert alert-success">
            {{ legalNatureResult.message }}
          </div>
        </div>

        <hr>

        <div class="maintenance-section">
          <h4>Áreas de terreno e edificações (por parque)</h4>
          <p class="help-text">
            Recria as respostas de áreas de terreno e edificações no questionário do parque
            a partir das Informações Gerais, quando estiverem inconsistentes.
            Equivalente ao comando <code>inovadata:fix-park-land-areas {parkId}</code>.
          </p>

          <div class="form-inline btn-row">
            <div class="form-group">
              <label for="parkIdInput">ID do parque</label>
              <input
                id="parkIdInput"
                v-model.number="parkId"
                type="number"
                min="1"
                class="form-control park-id-input"
                placeholder="Ex.: 73"
              >
            </div>
            <button
              class="btn btn-warning"
              @click="runLandAreasFix"
              :disabled="landAreasLoading || !parkId"
            >
              Corrigir áreas do parque &nbsp;
              <i class="fa fa-refresh fa-spin" v-if="landAreasLoading"></i>
            </button>
          </div>

          <div v-if="landAreasError" class="alert alert-danger">{{ landAreasError }}</div>

          <div
            v-if="landAreasResult"
            :class="landAreasResult.status === 'ok' ? 'alert alert-success' : 'alert alert-warning'"
          >
            {{ landAreasResult.message }}
          </div>
        </div>

        <hr>

        <div class="maintenance-section">
          <h4>Outliers financeiros (correção de respostas)</h4>
          <p class="help-text">
            Compara e corrige valores monetários do Park Quiz com base na planilha de ajustes
            empacotada na API. Isso <strong>atualiza o banco</strong> (<code>abstract_field_answer</code>).
            Não altera o <code>KnownFinancialOutlierRegistry</code>, que apenas exclui outliers
            conhecidos (Biopark, BIOTIC, Órion) no relatório estatístico.
          </p>

          <div class="checkbox btn-row">
            <label>
              <input type="checkbox" v-model="financialIncludeUnanswered">
              Incluir quizzes <code>unanswered</code> (ex.: Itaipu 2020)
            </label>
          </div>

          <div class="btn-row">
            <button
              class="btn btn-primary"
              @click="runFinancialAudit"
              :disabled="financialLoading"
            >
              Auditar (somente leitura) &nbsp;
              <i class="fa fa-refresh fa-spin" v-if="financialLoading && financialAction === 'audit'"></i>
            </button>

            <button
              class="btn btn-warning"
              @click="runFinancialFix"
              :disabled="financialLoading"
            >
              Corrigir outliers &nbsp;
              <i class="fa fa-refresh fa-spin" v-if="financialLoading && financialAction === 'fix'"></i>
            </button>
          </div>

          <div v-if="financialError" class="alert alert-danger">{{ financialError }}</div>

          <div v-if="financialResult" class="result-box">
            <p>
              <strong>{{ financialResult.fixApplied ? 'Correção executada.' : 'Auditoria executada.' }}</strong>
              Total: <strong>{{ financialResult.total }}</strong>
              <span v-if="!financialResult.fixApplied">
                — A atualizar: <strong>{{ financialResult.wouldUpdate }}</strong>
              </span>
              <span v-else>
                — Atualizados: <strong>{{ financialResult.updated }}</strong>
              </span>
              — Sem alteração: <strong>{{ financialResult.unchanged }}</strong>
              — Não encontrados: <strong>{{ financialResult.answerNotFound }}</strong>
              <span v-if="financialResult.parkNotFound">
                — Parque não encontrado: <strong>{{ financialResult.parkNotFound }}</strong>
              </span>
              <span v-if="financialResult.ambiguous">
                — Ambíguos: <strong>{{ financialResult.ambiguous }}</strong>
              </span>
            </p>

            <div class="table-responsive" v-if="financialVisibleRows.length">
              <table class="table table-striped table-bordered table-condensed">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Parque</th>
                    <th>Ano</th>
                    <th>Campo</th>
                    <th>Atual</th>
                    <th>Esperado</th>
                    <th>Delta</th>
                    <th>Quiz</th>
                    <th>Detalhe</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, index) in financialVisibleRows" :key="index">
                    <td><span class="label" :class="financialStatusClass(row.status)">{{ row.status }}</span></td>
                    <td>{{ row.parkLabel || row.parkNameContains }}</td>
                    <td>{{ row.year }}</td>
                    <td>{{ row.fieldLabel }}</td>
                    <td>{{ formatMoney(row.currentValue) }}</td>
                    <td>{{ formatMoney(row.expectedValue) }}</td>
                    <td>{{ formatMoney(row.delta) }}</td>
                    <td>{{ row.quizStatus || '—' }}</td>
                    <td>{{ row.detail }}</td>
                  </tr>
                </tbody>
              </table>
              <p v-if="financialHiddenUnchanged > 0" class="help-text">
                {{ financialHiddenUnchanged }} linha(s) <code>unchanged</code>/<code>skipped_pending</code> ocultas.
              </p>
            </div>

            <p v-else class="text-success">Nenhuma linha com delta ou problema a reportar.</p>
          </div>
        </div>
      </section>
    </div>
  </Adminlte>
</template>

<script>
  import Adminlte from '@/components/template/Adminlte'
  import MaintenanceClient from '@/services/resource_client/maintenance'

  export default {
    name: 'ParkStageAudit',
    components: { Adminlte },
    data () {
      return {
        parkStagesLoading: false,
        parkStagesAction: null,
        parkStagesResult: null,
        parkStagesError: null,
        legalNatureLoading: false,
        legalNatureResult: null,
        legalNatureError: null,
        parkId: null,
        landAreasLoading: false,
        landAreasResult: null,
        landAreasError: null,
        financialIncludeUnanswered: true,
        financialLoading: false,
        financialAction: null,
        financialResult: null,
        financialError: null
      }
    },
    computed: {
      financialVisibleRows () {
        if (!this.financialResult || !this.financialResult.rows) {
          return []
        }
        return this.financialResult.rows.filter(row => {
          return row.status !== 'unchanged' && row.status !== 'skipped_pending'
        })
      },
      financialHiddenUnchanged () {
        if (!this.financialResult || !this.financialResult.rows) {
          return 0
        }
        return this.financialResult.rows.length - this.financialVisibleRows.length
      }
    },
    methods: {
      parseMaintenanceError (error) {
        if (!error || !error.response) {
          return 'Erro ao conectar com a API. Verifique sua conexão e tente novamente.'
        }

        const status = error.response.status
        const apiMessage = error.response.data && error.response.data.message

        if (status === 401) {
          return 'Sessão expirada ou token inválido. Faça login novamente como administrador.'
        }
        if (status === 403) {
          return 'Seu usuário não tem permissão para executar rotinas de manutenção.'
        }
        if (status === 404) {
          return 'Endpoint de manutenção não encontrado. Verifique se a API foi atualizada e se o cache de produção (var/cache/prod) foi limpo após o deploy.'
        }
        if (apiMessage) {
          return apiMessage
        }

        return 'Não foi possível executar a rotina. Tente novamente mais tarde.'
      },
      runAudit () {
        this.executeParkStages('audit', () => MaintenanceClient.auditParkStages())
      },
      runSync () {
        if (!window.confirm('Isso irá atualizar o estágio cadastrado dos parques com base nas datas das Informações Gerais. Deseja continuar?')) {
          return
        }
        this.executeParkStages('sync', () => MaintenanceClient.syncParkStages())
      },
      runLegalNatureUpdate () {
        if (!window.confirm('Isso irá atualizar as opções de natureza jurídica no banco de dados. Deseja continuar?')) {
          return
        }

        this.legalNatureLoading = true
        this.legalNatureError = null
        this.legalNatureResult = null

        MaintenanceClient.updateManagementEntityLegalNatureOptions()
          .then(response => {
            this.legalNatureResult = response.data
          })
          .catch((error) => {
            this.legalNatureError = this.parseMaintenanceError(error)
          })
          .finally(() => {
            this.legalNatureLoading = false
          })
      },
      runLandAreasFix () {
        if (!this.parkId) {
          return
        }

        if (!window.confirm('Isso irá recriar as respostas de áreas do questionário com base nas Informações Gerais. Deseja continuar?')) {
          return
        }

        this.landAreasLoading = true
        this.landAreasError = null
        this.landAreasResult = null

        MaintenanceClient.fixParkLandAreas(this.parkId)
          .then(response => {
            this.landAreasResult = response.data
          })
          .catch((error) => {
            this.landAreasError = this.parseMaintenanceError(error)
          })
          .finally(() => {
            this.landAreasLoading = false
          })
      },
      executeParkStages (action, request) {
        this.parkStagesLoading = true
        this.parkStagesAction = action
        this.parkStagesError = null
        request()
          .then(response => {
            this.parkStagesResult = response.data
          })
          .catch((error) => {
            this.parkStagesError = this.parseMaintenanceError(error)
          })
          .finally(() => {
            this.parkStagesLoading = false
          })
      },
      runFinancialAudit () {
        this.executeFinancial('audit', () => MaintenanceClient.auditFinancialOutliers(this.financialIncludeUnanswered))
      },
      runFinancialFix () {
        if (!window.confirm('Isso irá atualizar valores monetários no banco conforme a planilha de outliers. Deseja continuar?')) {
          return
        }
        this.executeFinancial('fix', () => MaintenanceClient.fixFinancialOutliers(this.financialIncludeUnanswered))
      },
      executeFinancial (action, request) {
        this.financialLoading = true
        this.financialAction = action
        this.financialError = null
        request()
          .then(response => {
            this.financialResult = response.data
          })
          .catch((error) => {
            this.financialError = this.parseMaintenanceError(error)
          })
          .finally(() => {
            this.financialLoading = false
          })
      },
      formatMoney (value) {
        if (value === null || value === undefined || value === '') {
          return '—'
        }
        const number = Number(value)
        if (Number.isNaN(number)) {
          return String(value)
        }
        return number.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      },
      financialStatusClass (status) {
        if (!status) {
          return 'label-default'
        }
        if (status.indexOf('updated') === 0 || status === 'would_update' || status.indexOf('would_update') === 0) {
          return 'label-warning'
        }
        if (status === 'unchanged') {
          return 'label-success'
        }
        if (status === 'answer_not_found' || status === 'park_not_found' || status === 'ambiguous') {
          return 'label-danger'
        }
        return 'label-default'
      }
    }
  }
</script>

<style scoped>
  .maintenance-section {
    margin-bottom: 20px;
  }
  .maintenance-section h4 {
    color: #333;
    margin-top: 0;
  }
  .help-text {
    color: #555;
    margin-bottom: 15px;
  }
  .btn-row {
    margin-bottom: 15px;
  }
  .btn-row .btn {
    margin-right: 10px;
  }
  .result-box {
    margin-top: 15px;
  }
  .issues-list {
    margin: 0;
    padding-left: 18px;
  }
  .park-id-input {
    width: 120px;
    margin-left: 8px;
    margin-right: 12px;
  }
  .form-inline .form-group label {
    color: #555;
    font-weight: normal;
  }
  .table-responsive {
    max-height: 480px;
    overflow: auto;
  }
</style>



// WEBPACK FOOTER //
// ParkStageAudit.vue?009f94d8